import { memo, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const Button = memo(({
  children,
  onClick,
  disabled,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  style = {},
  ...props
}) => {
  const { colors } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const sizeStyles = {
    sm: {
      height: '32px',
      padding: '0 12px',
      fontSize: '0.75rem',
      borderRadius: '4px',
    },
    md: {
      height: '36px',
      padding: '0 14px',
      fontSize: '0.8125rem',
      borderRadius: '4px',
    },
    lg: {
      height: '40px',
      padding: '0 16px',
      fontSize: '0.875rem',
      borderRadius: '4px',
    },
  };

  const getVariantStyles = () => {
    const base = {
      primary: {
        background: colors.foreground,
        color: colors.background,
        border: 'none',
      },
      outline: {
        background: 'transparent',
        color: colors.foreground,
        border: `1px solid ${colors.border}`,
      },
      ghost: {
        background: 'transparent',
        color: colors.foreground,
        border: 'none',
      },
    };

    // Hover states - subtle, no harsh borders
    if (isHovered && !disabled) {
      if (variant === 'primary') {
        return { ...base.primary, opacity: 0.85 };
      }
      if (variant === 'outline') {
        return { ...base.outline, background: colors.backgroundOffset };
      }
      if (variant === 'ghost') {
        return { ...base.ghost, background: colors.backgroundOffset };
      }
    }

    return base[variant];
  };

  // Calculate opacity considering custom style opacity
  const customOpacity = style.opacity !== undefined ? style.opacity : 1;
  const finalOpacity = disabled ? 0.6 : customOpacity;

  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    fontWeight: 500,
    letterSpacing: '-0.005em',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s ease',
    outline: 'none',
    whiteSpace: 'nowrap',
    fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    transform: isHovered && !disabled ? 'translateY(-1px)' : (isActive && !disabled ? 'scale(0.98)' : 'none'),
    width: fullWidth ? '100%' : 'auto',
    ...sizeStyles[size],
    ...getVariantStyles(),
    ...style,
    opacity: finalOpacity,
  };

  return (
    <button
      style={baseStyle}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={() => setIsActive(true)}
      onMouseUp={() => setIsActive(false)}
      {...props}
    >
      {children}
    </button>
  );
});

export default Button;